// storageBilling.ts
// Pure helpers for the storage-cost UI (docs/STORAGE_RETENTION_PLAN.md, W5).
//
// Policy (decided 2026-09-28): uploads expire after 30 days, so storage is
// normally free. Only the monthly average above the free allowance is billed,
// at `creditsPerGBMonth` credits per GB-month (default 1 above 5 GB free).
// When the balance can't cover the charge, pinned files go back to normal
// deletion with at least STORAGE_UNPAID_NOTICE_DAYS of notice.
//
// Response contract of GET /api/storage/billing (backend W5a):
// { freeGB, creditsPerGBMonth,
//   currentPeriod: { period, avgGBSoFar, projectedGB, estimatedCredits, meteredDays },
//   lastCharge: { period, avgGB, billableGB, credits, paidCredits, unpaidCredits, chargedAt } | null,
//   nextChargeDate, storageUnpaid }

/** Binary GB, same as the backend's `byteDays / days / 1024³`. */
export const BYTES_PER_GB = 1024 ** 3;
/** Defaults mirrored from the backend env (STORAGE_FREE_GB, STORAGE_CREDITS_PER_GB_MONTH). */
export const DEFAULT_STORAGE_FREE_GB = 5;
export const DEFAULT_CREDITS_PER_GB_MONTH = 1;
/** Minimum notice before un-pinned files are deleted after an unpaid charge. */
export const STORAGE_UNPAID_NOTICE_DAYS = 14;

export interface StorageBillingPeriod {
  period: string;
  avgGBSoFar: number;
  projectedGB: number;
  estimatedCredits: number;
  meteredDays: number;
}

export interface StorageLastCharge {
  period: string;
  avgGB: number;
  billableGB: number;
  credits: number;
  paidCredits: number;
  unpaidCredits: number;
  chargedAt: string;
}

export interface StorageBilling {
  freeGB: number;
  creditsPerGBMonth: number;
  currentPeriod: StorageBillingPeriod | null;
  lastCharge: StorageLastCharge | null;
  nextChargeDate: string;
  storageUnpaid: boolean;
}

function num(v: unknown, fallback = 0): number {
  const n = typeof v === 'string' && v.trim() === '' ? NaN : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function nonNeg(v: unknown, fallback = 0): number {
  return Math.max(0, num(v, fallback));
}

function isObj(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/**
 * Validate/normalize the billing response. Returns null when the payload is
 * not the billing contract (e.g. an HTML 404 page, an empty body, an error
 * object), so the UI hides the cost section instead of showing zeros.
 * Tolerates an `{ Item: {...} }` wrapper and numeric strings.
 */
export function normalizeStorageBilling(raw: unknown): StorageBilling | null {
  let src: unknown = raw;
  if (isObj(src) && isObj(src.Item)) src = src.Item;
  if (!isObj(src)) return null;
  if (src.freeGB === undefined || src.freeGB === null || !Number.isFinite(num(src.freeGB, NaN))) return null;

  const cp = isObj(src.currentPeriod) ? src.currentPeriod : null;
  const lc = isObj(src.lastCharge) ? src.lastCharge : null;
  const credits = lc ? nonNeg(lc.credits) : 0;
  const paid = lc ? nonNeg(lc.paidCredits, credits) : 0;

  return {
    freeGB: nonNeg(src.freeGB, DEFAULT_STORAGE_FREE_GB),
    creditsPerGBMonth: nonNeg(src.creditsPerGBMonth, DEFAULT_CREDITS_PER_GB_MONTH),
    currentPeriod: cp
      ? {
        period: String(cp.period || ''),
        avgGBSoFar: nonNeg(cp.avgGBSoFar),
        projectedGB: nonNeg(cp.projectedGB),
        estimatedCredits: Math.ceil(nonNeg(cp.estimatedCredits)),
        meteredDays: Math.floor(nonNeg(cp.meteredDays)),
      }
      : null,
    lastCharge: lc
      ? {
        period: String(lc.period || ''),
        avgGB: nonNeg(lc.avgGB),
        billableGB: nonNeg(lc.billableGB),
        credits,
        paidCredits: paid,
        unpaidCredits: nonNeg(lc.unpaidCredits, Math.max(0, credits - paid)),
        chargedAt: String(lc.chargedAt || ''),
      }
      : null,
    nextChargeDate: String(src.nextChargeDate || ''),
    storageUnpaid: src.storageUnpaid === true || src.storageUnpaid === 'true',
  };
}

export function bytesToGB(bytes: number | null | undefined): number {
  return nonNeg(bytes) / BYTES_PER_GB;
}

/** GB of current usage above the free allowance (never negative). */
export function gbAboveFree(usedBytes: number | null | undefined, freeGB: number): number {
  return Math.max(0, bytesToGB(usedBytes) - nonNeg(freeGB));
}

/** Share of the free allowance in use, clamped to [0, 1] (0 when no allowance). */
export function freeAllowanceFraction(usedBytes: number | null | undefined, freeGB: number): number {
  const free = nonNeg(freeGB);
  if (free <= 0) return bytesToGB(usedBytes) > 0 ? 1 : 0;
  return Math.min(1, bytesToGB(usedBytes) / free);
}

/**
 * Credits for the Navigation hint ("Storage: ~N credits/month"). Returns 0
 * when there is nothing to pay (hint hidden) or billing is unavailable.
 */
export function estimatedMonthlyCredits(billing: StorageBilling | null | undefined): number {
  return billing?.currentPeriod ? Math.max(0, billing.currentPeriod.estimatedCredits) : 0;
}

// vue-i18n locale codes → BCP 47 for Intl (cn is not a region-less tag).
const LOCALE_MAP: Record<string, string> = { cn: 'zh-CN' };
function tagFor(locale?: string): string | undefined {
  return (locale && LOCALE_MAP[locale]) || locale || undefined;
}

/** "7.4 GB" — 2 decimals under 1 GB, 1 decimal otherwise, localized separator. */
export function formatGB(gb: number | null | undefined, locale?: string): string {
  const v = nonNeg(gb);
  const digits = v > 0 && v < 1 ? 2 : 1;
  let s: string;
  try {
    s = new Intl.NumberFormat(tagFor(locale), { maximumFractionDigits: digits }).format(v);
  } catch {
    s = String(parseFloat(v.toFixed(digits)));
  }
  return `${s} GB`;
}

/** "2026-09" → "September 2026" (UTC, localized). Unparseable input is returned as-is. */
export function formatBillingPeriod(period: string | null | undefined, locale?: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(String(period || ''));
  if (!m) return String(period || '');
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
  const opts: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric', timeZone: 'UTC' };
  try {
    return d.toLocaleDateString(tagFor(locale), opts);
  } catch {
    return d.toLocaleDateString(undefined, opts);
  }
}

/** HTTP status of a failed request (axios error shape), or 0. */
export function errorStatus(error: unknown): number {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === 'number' ? status : 0;
}
